/*
 * SearchFilter
 * @module Search
 * @flow
 */

import React, { Fragment } from 'react';
import styled, { css } from 'styled-components/macro';
import { ifProp } from 'styled-tools';
import { subDays, startOfDay } from 'date-fns';
import { FormattedMessage, type InjectIntlProvidedProps } from 'react-intl';
import * as R from 'ramda';

import { parseBool } from '~/helpers';

import { Input } from '~/components/Input';
import { Select } from '~/components/Select';
import { Checkbox } from '~/components/Checkbox';
// $FlowIssue
import { ReactComponent as IconCalendar } from '@/icons/ic-calendar.svg';

import { SearchFilterCondition } from './components';

import messages from './messages';

type SearchFilterType =
  | 'numeric'
  | 'date'
  | 'select'
  | 'input'
  | 'rule'
  | 'checkbox';

export type SearchProps = InjectIntlProvidedProps & {
  children?: React$Node,
  error?: React$Node | string,
  value?: number[] | string | number,
  options?:
    | number[]
    | {|
        value: string | number,
        label: React$Node,
      |}[],
  type: SearchFilterType,
  name: string,
  onChange: (string, SearchFilterType, string | number) => void,
  onFocus: (SyntheticEvent<HTMLInputElement>) => void,
  device?: $PropertyType<DeviceQuery, 'device'>,
};

const SearchFilterRange = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;

  & > div[role='input'] {
    flex: 1;
    padding-top: 0;
    min-width: 118px;
  }

  & > span {
    display: inline-flex;
    margin: 0 10px;
  }
`;

const SearchFilterTitleWrapper = styled.div`
  ${ifProp(
    { device: 'desktop' },
    css`
      display: inline-flex;
      align-items: center;
      padding: 10px 0 20px;
      min-width: 160px;
      font-weight: 600;
    `,
    css`
      margin-bottom: 4px;
      font-size: 14px;
    `,
  )}
`;

const StyledInput = styled(Input)`
  padding-top: 0;
  width: 100%;
`;

const SearchFilterWrapper = styled.div.attrs(() => ({
  className: 'react-search-filter',
}))`
  ${ifProp(
    { device: 'desktop' },
    css`
      display: flex;
    `,
  )}

  ${/* sc-selector */ Select} {
    width: 100%;

    & > .react-select__control {
      border-width: 1px;
      border-radius: 16px;
      min-height: 32px;
      margin-bottom: 20px;

      & > .react-select__indicators {
        margin-right: 5px;
      }
    }
  }
`;

/**
 * SearchFilter React-component
 * @memberof Search
 * @param {Object} props - properties
 * @param {React.Children} [props.children] - childs
 * @example <caption>Simple usage</caption>
 * <SearchFilter value={[1, 2]} type="numeric">Deposit Returned</SearchFilter>
 * @returns {React.Node<*>} React-component
 */
export const SearchFilter = ({
  children,
  options,
  type,
  error,
  value,
  onChange,
  onFocus,
  intl,
  // $FlowIssue
  ...rest
}: SearchProps) => {
  let content;

  if (type === 'checkbox') {
    content = (
      <Checkbox
        checked={parseBool(value)}
        id={rest.name}
        onChange={value => onChange(rest.name, type, value)}
        {...rest}
      >
        {children}
      </Checkbox>
    );
  } else {
    content = (
      <Fragment>
        <SearchFilterTitleWrapper>
          {children ? <Fragment>{children}</Fragment> : null}
        </SearchFilterTitleWrapper>
        {(() => {
          if (type === 'rule') {
            return (
              <SearchFilterCondition
                options={options}
                value={value}
                id={rest.name}
                onChange={value => onChange(rest.name, type, value)}
                {...rest}
              />
            );
          }

          if (type === 'select') {
            return (
              <Select
                options={options}
                value={R.find(R.propEq('value', value))(options || [])}
                id={rest.name}
                onChange={({ value }) => onChange(rest.name, type, value)}
                {...rest}
              />
            );
          }

          if (type === 'input') {
            return (
              <StyledInput
                size="small"
                {...rest}
                data-type={type}
                value={value}
                onChange={value => onChange(rest.name, type, value)}
                id={rest.name}
                onFocus={onFocus}
                error={[error]}
              />
            );
          }

          const isDate = type === 'date';

          let range = options;
          let onRightIconClick;

          if (!Array.isArray(range)) {
            if (!isDate) {
              range = [undefined, undefined];
            } else {
              const now = new Date();
              const yesterday = subDays(startOfDay(now), 1);

              range = [yesterday, now];
            }
          }

          let from = R.head(Array.isArray(value) ? value : range);
          let to = R.last(Array.isArray(value) ? value : range);

          if (isDate) {
            from = +from;
            to = +to;
            onRightIconClick = id => () => {
              const element = document.getElementById(id);

              if (element) {
                element.focus();
              }
            };
          }

          return (
            <Fragment>
              <SearchFilterRange>
                <Input
                  size="small"
                  {...rest}
                  data-direction={0}
                  data-type={type}
                  data-value={from}
                  value={isDate ? intl.formatDate(from) : from}
                  onChange={value => onChange(`${rest.name}.0`, type, value)}
                  id={`${rest.name}.0`}
                  onFocus={onFocus}
                  rightIcon={isDate && <IconCalendar />}
                  onRightIconClick={
                    onRightIconClick && onRightIconClick(`${rest.name}.0`)
                  }
                  placeholder={<FormattedMessage {...messages.from} />}
                  readOnly={isDate}
                  onlyPlaceholder
                  type={isDate ? 'text' : 'number'}
                />
                <span>—</span>
                <Input
                  size="small"
                  {...rest}
                  data-direction={1}
                  data-type={type}
                  data-value={to}
                  value={isDate ? intl.formatDate(to) : to}
                  onChange={value => onChange(`${rest.name}.1`, type, value)}
                  id={`${rest.name}.1`}
                  onFocus={onFocus}
                  rightIcon={isDate && <IconCalendar />}
                  onRightIconClick={
                    onRightIconClick && onRightIconClick(`${rest.name}.1`)
                  }
                  placeholder={<FormattedMessage {...messages.to} />}
                  readOnly={isDate}
                  onlyPlaceholder
                  type={isDate ? 'text' : 'number'}
                />
              </SearchFilterRange>
            </Fragment>
          );
        })()}
      </Fragment>
    );
  }

  return <SearchFilterWrapper>{content}</SearchFilterWrapper>;
};

SearchFilter.defaultProps = {
  palette: 'primary',
  type: 'numeric',
  onFocus: () => {},
};
